import { useMemo } from "react";
import queryString from "query-string";
import { useFetcher } from "~/hooks/useFetcher";
import { TableAccessLevel } from "~/pages/datasets/types/enums/access-level";
import SearchResponse from "~/types/interfaces/catalogResponse";
import { V1TableListEnvelope, V1TableSearchResult } from "~/types/interfaces/catalogTables";

const { VITE_CATALOG_API } = import.meta.env;

// The response key for the schema aggregation. The request asks for `schema`, but the
// response is keyed by the underlying OpenSearch field, so the two differ by design.
const SCHEMA_AGGREGATION_KEY = "databaseSchema.name";

// The v1 /tables/search envelope, plus the faceted-search aggregations.
type V1TableSearchResponse = V1TableListEnvelope<V1TableSearchResult> & {
  aggregations?: SearchResponse["aggregations"];
};

export interface TableSchema {
  name: string;
  /** Tables in this schema matching the current query, from the aggregation bucket. */
  tableCount: number;
}

/**
 * Lists the schemas holding tables the caller can see, with a per-schema count of the
 * tables matching the current search term.
 *
 * Reads the schema facet instead of the tables themselves: `limit=0` returns the
 * aggregation without a page of results, and `aggregation=schema` skips the
 * entity-type and tag aggregations this view never reads. Tables are paged separately
 * per schema, on expansion, by useSchemaTables.
 *
 * `searchTerm` must already be debounced. The caller owns the timing so this hook and
 * useSchemaTables share one timer and can never disagree about the current term.
 */
const useTableSchemas = (searchTerm: string, accessLevel?: TableAccessLevel) => {
  const qs = queryString.stringify({
    // The endpoint does not normalize a blank term, so send the match-everything
    // wildcard explicitly.
    query_term: searchTerm || "*",
    limit: 0,
    aggregation: "schema",
    // Only include min_access_level when an accessLevel is provided
    ...(accessLevel && { min_access_level: accessLevel }),
  });

  const resp = useFetcher<V1TableSearchResponse>(`${VITE_CATALOG_API}/v1/tables/search?${qs}`, {
    keepPreviousData: true,
  });

  const aggregation = resp.data?.aggregations?.[SCHEMA_AGGREGATION_KEY];
  const buckets = aggregation?.buckets;

  const schemas = useMemo<TableSchema[]>(
    () =>
      (buckets ?? [])
        .map((bucket) => ({
          name: bucket.key,
          tableCount: bucket.doc_count,
        }))
        // The aggregation orders buckets by match count descending, which is not a
        // useful order to browse. Sorting the mapped copy leaves the response untouched.
        .sort((a, b) => a.name.localeCompare(b.name)),
    [buckets]
  );

  return {
    ...resp,
    schemas,
    // The backend caps the schema aggregation's bucket count. Anything counted outside
    // those buckets is a schema the tree cannot show, so report it rather than
    // presenting a silently short list as complete.
    hasOmittedSchemas: (aggregation?.sum_other_doc_count ?? 0) > 0,
  };
};

export default useTableSchemas;
